import { CloseIcon, IconSizeEnum } from '@acceso/design-system';
import { CSSProperties, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { normalizeRect, rectToBbox, toRelativeRect } from '@/helpers/relative-coords';
import { getVideoFrame } from '@/helpers/video-frame';
import { TRect } from '@/types';

type TVideoCanvasProps = {
  drawerLogo: boolean;
  onDrawnRect?: (rect: TRect) => void;
  containerRef?: RefObject<HTMLDivElement>;
  videoRef?: RefObject<HTMLVideoElement | null>;
};

const clampUnit = (value: number) => Math.min(Math.max(value, 0), 1);

// A stroke released outside the layer ends with a click on the closest common ancestor,
// the player, which would toggle playback. Swallow that one click, wherever it lands.
const swallowStrokeClick = () => {
  const listeners: [string, EventListener][] = [];
  const release = () =>
    listeners.forEach(([type, listener]) => window.removeEventListener(type, listener, true));

  listeners.push(
    ['click', event => event.stopPropagation()],
    ['mouseup', () => setTimeout(release, 0)],
    ['mousedown', release]
  );
  listeners.forEach(([type, listener]) =>
    window.addEventListener(type, listener, { capture: true, once: true })
  );
};

export function VideoCanvas({
  drawerLogo,
  onDrawnRect,
  containerRef,
  videoRef,
}: TVideoCanvasProps) {
  const { t } = useTranslation();
  const { setDrawerLogo } = useBroadcastScheduleProgram();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const referenceRectRef = useRef<DOMRect | null>(null);

  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [currentRect, setCurrentRect] = useState<TRect | null>(null);
  const [drawnRect, setDrawnRect] = useState<TRect[]>([]);
  const [layerStyle, setLayerStyle] = useState<CSSProperties | null>(null);

  // Boxes are displayed against the <video> element (see VideoBoxes), so they must be
  // measured against it too; the container is taller because of the control bar strip.
  const getReferenceRect = useCallback((): DOMRect | null => {
    const video = videoRef?.current;
    if (video) return video.getBoundingClientRect();

    const container = containerRef?.current;
    if (container) return container.getBoundingClientRect();

    return canvasRef.current?.getBoundingClientRect() ?? null;
  }, [containerRef, videoRef]);

  const updateLayerStyle = useCallback(() => {
    const container = containerRef?.current;
    const video = videoRef?.current;
    if (!container || !video) {
      setLayerStyle(null);
      return;
    }

    const frame = getVideoFrame(container, video);
    if (!frame) {
      setLayerStyle(null);
      return;
    }

    setLayerStyle({
      left: Math.floor(frame.left),
      top: Math.floor(frame.top),
      width: Math.floor(frame.width),
      height: Math.floor(frame.height),
    });
  }, [containerRef, videoRef]);

  useEffect(() => {
    const container = containerRef?.current;
    const observer = new window.ResizeObserver(() => updateLayerStyle());
    let observedVideo: HTMLVideoElement | null = null;

    const followVideo = () => {
      const video = videoRef?.current ?? null;
      if (video !== observedVideo) {
        if (observedVideo) observer.unobserve(observedVideo);
        if (video) observer.observe(video);
        observedVideo = video;
      }
      updateLayerStyle();
    };

    followVideo();
    if (container) {
      observer.observe(container);
      container.addEventListener('loadedmetadata', followVideo, true);
    }

    return () => {
      observer.disconnect();
      container?.removeEventListener('loadedmetadata', followVideo, true);
    };
  }, [containerRef, videoRef, updateLayerStyle]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawerLogo) return;

    const reference = getReferenceRect();
    if (!reference) return;

    referenceRectRef.current = reference;
    swallowStrokeClick();
    setCurrentRect([e.clientX - reference.left, e.clientY - reference.top, 0, 0]);
    setIsDrawing(true);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const reference = referenceRectRef.current;
    if (!drawerLogo || !isDrawing || !currentRect || !reference) return;

    const currentX = e.clientX - reference.left;
    const currentY = e.clientY - reference.top;
    setCurrentRect([
      currentRect[0],
      currentRect[1],
      currentX - currentRect[0],
      currentY - currentRect[1],
    ]);
  };

  const handleDrawnEnd = () => {
    const reference = referenceRectRef.current;
    if (!drawerLogo || !isDrawing || !currentRect || !reference) return;

    const normalizedRect = normalizeRect(currentRect);
    setIsDrawing(false);
    setCurrentRect(null);

    // A zero-sized reference would turn the box into NaN, which clamping cannot fix.
    if (!reference.width || !reference.height) return;

    const relativeRect = toRelativeRect(normalizedRect, reference.width, reference.height);
    setDrawnRect([normalizedRect]);
    onDrawnRect?.(rectToBbox(relativeRect).map(clampUnit) as TRect);
  };

  const handleMouseUp = () => {
    if (!drawerLogo || !isDrawing || !currentRect) return;
    handleDrawnEnd();
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Limpiar
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Escala entre tamaño CSS y tamaño interno del canvas
    const scaleX = canvas.width / canvas.offsetWidth;
    const scaleY = canvas.height / canvas.offsetHeight;

    const canvasRect = canvas.getBoundingClientRect();
    const reference = referenceRectRef.current;
    const offsetX = reference ? reference.left - canvasRect.left : 0;
    const offsetY = reference ? reference.top - canvasRect.top : 0;

    // Dibujar rectángulos ya dibujados
    drawnRect.forEach(rect => {
      const [x, y, w, h] = rect;
      ctx.beginPath();
      ctx.rect((x + offsetX) * scaleX, (y + offsetY) * scaleY, w * scaleX, h * scaleY);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'red';
      ctx.stroke();
    });

    // Dibujar rectángulo actual mientras se dibuja
    if (isDrawing && currentRect) {
      const [x, y, w, h] = normalizeRect(currentRect);
      ctx.beginPath();
      ctx.rect((x + offsetX) * scaleX, (y + offsetY) * scaleY, w * scaleX, h * scaleY);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'blue';
      ctx.stroke();
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      drawCanvas();
    }
  }, [drawnRect, currentRect, isDrawing, drawerLogo, layerStyle]);

  const handleCloseCanvas = () => {
    setDrawnRect([]);
    setDrawerLogo(false);
    containerRef?.current?.focus();
  };

  return (
    <div
      className={layerStyle ? 'absolute' : 'absolute inset-0'}
      style={{ ...layerStyle, zIndex: 10 }}
      onClick={e => e.stopPropagation()}
    >
      <canvas
        ref={canvasRef}
        aria-label={t('Área de dibujo del cuadro delimitador')}
        className="absolute left-0 top-0 h-full w-full bg-slate-50/70"
        style={{ cursor: 'crosshair' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
      <button
        onClick={handleCloseCanvas}
        aria-label="Cerrar canvas"
        className="absolute right-0 top-0 z-20"
      >
        <CloseIcon size={IconSizeEnum.SM} />
      </button>
    </div>
  );
}
